import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../services/api';
import StatusChip from '../components/StatusChip';
import { formatDuration, todayString, yesterdayString, getInitials } from '../utils/format';
import {
    Box, Typography, Paper, Avatar, Grid, ButtonGroup, Button, Alert,
    Skeleton, Stack, IconButton, List, ListItem, ListItemText,
} from '@mui/material';
import { ArrowBackRounded, PublicRounded, AppsRounded } from '@mui/icons-material';

const TODAY = todayString();
const YESTERDAY = yesterdayString();

function UsageList({ title, icon, items, emptyLabel }) {
    return (
        <Paper sx={{ p: 3, height: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                {icon}
                <Typography variant="h4">{title}</Typography>
            </Stack>
            {items.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 2 }}>{emptyLabel}</Typography>
            ) : (
                <List dense disablePadding>
                    {items.slice(0, 15).map((item, i) => (
                        <ListItem
                            key={i}
                            disableGutters
                            secondaryAction={<StatusChip status={item.status} />}
                            sx={{ py: 1, borderBottom: i < items.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}
                        >
                            <ListItemText
                                primary={item.domain || item.app_name}
                                secondary={formatDuration(item.usage_time)}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                            />
                        </ListItem>
                    ))}
                </List>
            )}
        </Paper>
    );
}

export default function EmployeeDetail() {
    const { user_id } = useParams();
    const navigate = useNavigate();

    const [date, setDate]       = React.useState(TODAY);
    const [data, setData]       = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError]     = React.useState('');

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        dashboardAPI.getEmployeeDetail(user_id, date)
            .then((res) => {
                if (cancelled) return;
                if (res.data.error === '1') throw new Error(res.data.message || 'Failed to load employee');
                setData(res.data);
            })
            .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load employee'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [user_id, date]);

    const employee = data?.employee;
    const log = data?.log;
    const shift = data?.shift;

    return (
        <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                <IconButton onClick={() => navigate('/employees')} size="small">
                    <ArrowBackRounded />
                </IconButton>
                <Typography variant="h2">Employee Detail</Typography>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {loading && !data ? (
                <Skeleton variant="rectangular" height={140} sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 3, mb: 3 }} />
            ) : employee ? (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{
                                width: 56, height: 56, fontSize: '1.1rem', fontWeight: 700,
                                background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                            }}>
                                {getInitials(employee.name)}
                            </Avatar>
                            <Box>
                                <Typography variant="h3">{employee.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {employee.email} · {employee.employee_id}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    App version {employee.version}
                                </Typography>
                            </Box>
                        </Stack>
                        <ButtonGroup size="small" variant="outlined">
                            <Button variant={date === TODAY ? 'contained' : 'outlined'} onClick={() => setDate(TODAY)}>Today</Button>
                            <Button variant={date === YESTERDAY ? 'contained' : 'outlined'} onClick={() => setDate(YESTERDAY)}>Yesterday</Button>
                        </ButtonGroup>
                    </Stack>
                </Paper>
            ) : null}

            {/* Summary cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Attendance</Typography>
                        {loading ? <Skeleton variant="text" width={100} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                            : <StatusChip status={data?.attendance} />}
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Worked Time</Typography>
                        {loading ? <Skeleton variant="text" width={100} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                            : <Typography variant="h3">{log ? formatDuration(log.total_usage) : '—'}</Typography>}
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Idle Time</Typography>
                        {loading ? <Skeleton variant="text" width={100} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                            : <Typography variant="h3">{log ? formatDuration(log.idle_time) : '—'}</Typography>}
                    </Paper>
                </Grid>
            </Grid>

            {shift && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Shift</Typography>
                    <Typography variant="body1">
                        {shift.shift_title || shift.shift_type} · {shift.start_time}–{shift.end_time}
                    </Typography>
                </Paper>
            )}

            {/* Usage breakdown */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <UsageList
                        title="Website Usage"
                        icon={<PublicRounded fontSize="small" sx={{ color: 'text.secondary' }} />}
                        items={data?.web_usage || []}
                        emptyLabel="No website activity for this date."
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <UsageList
                        title="Application Usage"
                        icon={<AppsRounded fontSize="small" sx={{ color: 'text.secondary' }} />}
                        items={data?.app_usage || []}
                        emptyLabel="No application activity for this date."
                    />
                </Grid>
            </Grid>
        </Box>
    );
}
